import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { i18n } from '@/shared/i18n';
import { CONNECTOR_PLATFORM_LABELS } from '@/entities/media-connector';
import {
  levelLabel,
  type JobDeliveryState,
  type JobPublishNodeResult,
  type JobPublishOutcome,
  type JobPublishReport,
  type JobStatus,
} from '@/entities/media-campaign';
import { useTranslation } from 'react-i18next';
import { DELIVERY_LABELS } from './delivery-labels';

const DELIVERY_VARIANT: Record<
  JobDeliveryState,
  'positive' | 'secondary' | 'ghost' | 'warning' | 'destructive'
> = {
  active: 'positive',
  paused: 'secondary',
  draft: 'secondary',
  in_review: 'warning',
  rejected: 'destructive',
  ended: 'ghost',
  unknown: 'ghost',
};

// Getters, not values: a resolved label here would freeze English at import.
const OUTCOME_LABELS: Record<JobPublishOutcome, string> = {
  get created() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.outcome.created');
  },
  get updated() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.outcome.updated');
  },
  get unchanged() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.outcome.noChange');
  },
  get skipped() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.outcome.skipped');
  },
  get failed() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.outcome.failed');
  },
};

const OUTCOME_VARIANT: Record<
  JobPublishOutcome,
  'positive' | 'secondary' | 'ghost' | 'warning' | 'destructive'
> = {
  created: 'positive',
  updated: 'secondary',
  unchanged: 'ghost',
  skipped: 'warning',
  failed: 'destructive',
};

/** Only the three statuses a finished publish can leave behind. */
const PUBLISH_TITLES: Partial<Record<JobStatus, string>> = {
  get published() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.title.published');
  },
  get partially_published() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.title.publishedInPart');
  },
  get failed() {
    return i18n.t('projects:deliveryJobReview.publishReportDialog.title.nothingWasPublished');
  },
};

/** The counts worth stating, dropping the zeroes so the line stays readable. */
function summarise(report: JobPublishReport): string {
  /* eslint-disable i18next/no-literal-string -- resource-key suffixes, not copy */
  return (
    [
      [report.created, 'created'],
      [report.updated, 'updated'],
      [report.unchanged, 'unchanged'],
      [report.failed, 'failed'],
      [report.skipped, 'skipped'],
    ] as const
  )
    .filter(([count]) => count > 0)
    .map(([count, key]) =>
      i18n.t(`projects:deliveryJobReview.publishReportDialog.count.${key}`, { count }),
    )
    .join(' · ');
  /* eslint-enable i18next/no-literal-string */
}

/**
 * What the platforms turned out to be doing, once someone has asked.
 *
 * Deliberately a second line rather than folded into the first: what we managed to
 * write and what the platform then decided are different facts, and a plan can be
 * "12 created" and "9 refused" at the same time.
 */
function summariseDelivery(report: JobPublishReport): string | null {
  /* eslint-disable i18next/no-literal-string -- resource-key suffixes, not copy */
  const counts = (
    [
      [report.live, 'live'],
      [report.inReview, 'inReview'],
      [report.rejected, 'refused'],
    ] as const
  ).filter(([count]) => count > 0);
  /* eslint-enable i18next/no-literal-string */

  if (counts.length === 0) return null;
  return i18n.t('projects:deliveryJobReview.publishReportDialog.onThePlatform', {
    counts: counts
      .map(([count, key]) =>
        i18n.t(`projects:deliveryJobReview.publishReportDialog.deliveryCount.${key}`, { count }),
      )
      .join(' · '),
  });
}

/**
 * A creative has no entry in the platform's level schema — it is not a row in the
 * review table — so it is named directly rather than looked up.
 */
function publishLevelLabel(result: JobPublishNodeResult): string {
  return result.level === 'creative'
    ? i18n.t('projects:deliveryJobReview.publishReportDialog.creative')
    : levelLabel(result.platform, result.level);
}

const NEEDS_ATTENTION: JobPublishOutcome[] = ['failed', 'skipped'];

/**
 * Whether a row is remaining work.
 *
 * An ad the platform refused after the fact belongs at the top exactly as much as one
 * that never published: both are objects that will not serve, and the reviewer's next
 * action is the same in either case.
 */
function needsAttention(result: JobPublishNodeResult): boolean {
  return NEEDS_ATTENTION.includes(result.outcome) || result.deliveryState === 'rejected';
}

export interface PublishReportDialogProps {
  report: JobPublishReport | null;
  /** Absent while nothing has been published, which is when there is nothing to ask about. */
  onCheckDelivery?: () => void;
  isCheckingDelivery?: boolean;
  onClose: () => void;
}

/**
 * What the platforms actually did, node by node.
 *
 * A partial publish is a normal outcome here — an object the platform rejects takes
 * its subtree with it while its siblings still go out — so the report has to make the
 * remaining work findable rather than just report a count. Failures and the nodes they
 * stopped float to the top for that reason; everything else keeps plan order.
 */
export function PublishReportDialog({
  report,
  onCheckDelivery,
  isCheckingDelivery = false,
  onClose,
}: PublishReportDialogProps) {
  const { t } = useTranslation('projects');
  const attention = report?.results.filter(needsAttention) ?? [];
  const done = report?.results.filter((result) => !needsAttention(result)) ?? [];
  const delivery = report ? summariseDelivery(report) : null;

  return (
    <Dialog open={report !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-[min(calc(100vw-2rem),44rem)]"
        data-testid="job-publish-report-dialog"
      >
        <DialogHeader>
          <DialogTitle>{report ? PUBLISH_TITLES[report.status] : ''}</DialogTitle>
          <DialogDescription>{report ? summarise(report) : ''}</DialogDescription>
          {delivery && (
            <span className="text-sm text-muted-foreground" data-testid="job-delivery-summary">
              {delivery}
            </span>
          )}
        </DialogHeader>

        <div className="flex max-h-[26rem] flex-col gap-1 overflow-y-auto">
          {[...attention, ...done].map((result) => (
            <div
              key={result.nodeId}
              className="flex items-start justify-between gap-3 rounded-lg border border-border-light px-3 py-2"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-sm">{result.name}</span>
                <span className="text-xs text-muted-foreground">
                  {CONNECTOR_PLATFORM_LABELS[result.platform]} · {publishLevelLabel(result)}
                </span>
                {result.reason && (
                  <span
                    className={`text-xs ${
                      result.outcome === 'failed' ? 'text-destructive' : 'text-warning-foreground'
                    }`}
                  >
                    {result.reason}
                  </span>
                )}
                {/* Only where it adds something. On anything the platform simply accepted
                    the detail repeats the badge, and every row carrying "PAUSED" would
                    bury the handful that say why an ad was refused. */}
                {result.deliveryState === 'rejected' && result.deliveryDetail && (
                  <span className="text-xs text-destructive">{result.deliveryDetail}</span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Badge
                  variant={OUTCOME_VARIANT[result.outcome]}
                  roundness="full"
                  className="font-normal"
                >
                  {OUTCOME_LABELS[result.outcome]}
                </Badge>
                {result.deliveryState && (
                  <Badge
                    variant={DELIVERY_VARIANT[result.deliveryState]}
                    roundness="full"
                    className="font-normal"
                  >
                    {DELIVERY_LABELS[result.deliveryState]}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          {onCheckDelivery && (
            <Button
              variant="outline"
              size="default"
              onClick={onCheckDelivery}
              disabled={isCheckingDelivery}
              className="mr-auto"
              data-testid="job-check-delivery"
            >
              {isCheckingDelivery
                ? t('deliveryJobReview.publishReportDialog.checking')
                : t('deliveryJobReview.publishReportDialog.checkWhatLanded')}
            </Button>
          )}
          <Button size="default" onClick={onClose} data-testid="job-publish-report-close">
            {t('deliveryJobReview.publishReportDialog.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
